
import { COUNTRIES, LANGUAGES } from '@/constants';
import { Risolto_Channel_Enum } from '@/generated/graphql';
import axios from 'axios';
import Utils from '@/utils/utils';
import { EmailEndpointDetails, PhoneEndpointDetails, MailEndpointDetails, EmailAddressType, EndpointDetails } from '@/schema/channel_endpoint';
import Vue from 'vue';
import { recipientUpdateChannelEndpoint } from '@/api/recipient';

export default Vue.extend({
	props: {
		visible: { type: Boolean, required: true},
		participant: { type: String, required: true },
		legalEntity: { type: String, required: true },
		recipient: { type: String, required: true },
		contact: { type: String, required: false, default: undefined },
		dialogClose: { type: Function, required: false, default: undefined },
		endpoint: { type: Object, required: false, default: () => null }
	},
	data() {
		return {
			loading: false,
			form: {
				copy_to_risolto: false,
				channel: Risolto_Channel_Enum.Email,
				language: 'nl',
				email: '',
				telephone_number: '',
				invoicing: true,
				invoicing_type: 'TO',
				dunning: true,
				dunning_type: 'TO',
				city: '',
				zip_code: '',
				street_name: '',
				street_number: '',
				post_box: '',
				iso_country_code: 'BE',
				company_name: ''
			},
			phoneInvalid: false,
			languages: LANGUAGES,
			channels: [
				Risolto_Channel_Enum.Email,
				Risolto_Channel_Enum.Phone,
				Risolto_Channel_Enum.Mail
			]
		};
	},
	computed: {
		rules() {
			switch (this.form.channel) {
				case Risolto_Channel_Enum.Email:
					return {
						email: [{ required: true, message: this.$t('ChannelEndpointDialog.email_required'), trigger: 'blur' }]
					};
				case Risolto_Channel_Enum.Phone:
					return {
						phone: [{ validator: (rule, value, callback) => {
							if (!this.form.telephone_number) {
								return callback(new Error(this.$t('ChannelEndpointDialog.phone_required')));
							}
							callback();
						}}]
					};
				case Risolto_Channel_Enum.Mail:
					return {
						street_name: [{ required: true, message: this.$t('ChannelEndpointDialog.street_name_required'), trigger: 'blur' }],
						street_number: [{ required: true, message: this.$t('ChannelEndpointDialog.street_number_required'), trigger: 'blur' }],
						zip_code: [{ required: true, message: this.$t('ChannelEndpointDialog.postal_code_required'), trigger: 'blur' }],
						iso_country_code: [{ required: true, message: this.$t('ChannelEndpointDialog.iso_country_code_required'), trigger: 'change' }]
					};
				default:
					return {};
			}
		},
		title() {
			if (this.endpoint) {
				return this.$t('ChannelEndpointDialog.title_edit');
			} else {
				return this.$t('ChannelEndpointDialog.title_create');
			}
		},
		countries() {
			let countries = COUNTRIES;
			// Translate country names
			countries.forEach(country => {
				country.name = this.$translate.iso_country_code(country.code);
			});
			// Sort by country name
			countries.sort((a, b) => {
				return a.name.localeCompare(b.name);
			});

			return countries;
		},
		disabled() {
			return !this.form.copy_to_risolto && this.endpoint?.reference != null && this.endpoint?.origin !== 'RISOLTO';
		}
	},
	watch: {
		'endpoint': {
			handler(newVal, oldVal) {
				if (newVal) this.populateFormForEdit(newVal);
				else this.form = this.getDefaultFormState();
			},
			immediate: true,
			deep: true
		}
	},
	methods: {
		getDefaultFormState() {
			return {
				reference: null,
				copy_to_risolto: false,
				channel: Risolto_Channel_Enum.Email,
				language: 'nl',
				email: '',
				telephone_number: '',
				invoicing: true,
				invoicing_type: 'TO',
				dunning: true,
				dunning_type: 'TO',
				city: '',
				zip_code: '',
				street_name: '',
				street_number: '',
				post_box: '',
				iso_country_code: 'BE',
				company_name: ''
			};
		},

		populateFormForEdit(endpoint) {
			const addr = endpoint.endpoint_details?.address;
			this.form = {
				...this.getDefaultFormState(),
				reference: endpoint.reference,
				channel: endpoint.channel,
				language: endpoint.locale.substring(0, 2),
				email: endpoint.endpoint_details?.email || '',
				telephone_number: endpoint.endpoint_details?.telephone_number || '',
				invoicing: endpoint.endpoint_details.invoicing,
				invoicing_type: endpoint.endpoint_details.invoicing_type,
				dunning: endpoint.endpoint_details.dunning,
				dunning_type: endpoint.endpoint_details.dunning_type,
				city: addr?.city || '',
				zip_code: addr?.zip_code || '',
				street_name: addr?.street_name || addr?.street_name_number_box || '',
				street_number: addr?.street_number || '',
				post_box: addr?.post_box || '',
				iso_country_code: addr?.iso_country_code || '',
				company_name: addr?.company_name || ''
			};
		},

		handlePhoneValidation(value) {
			if (!value.valid) {
				this.phoneInvalid = true;
			} else {
				this.phoneInvalid = false;
			}
		},

		handleChannelChange() {
			this.$refs['form'].resetFields();
		},

		handleReset() {
			this.$refs['form'].resetFields();
			this.form = this.getDefaultFormState();
		},

		handleClose() {
			if (this.dialogClose) {
				this.dialogClose();
			}

			this.handleReset();
		},

		handleFormSubmit() {
			this.$refs['form'].validate((valid) => {
				if (valid) {
					if (this.endpoint && this.form.copy_to_risolto) {
						this.createEndpoint();
					} else if (this.endpoint) {
						this.updateEndpoint();
					} else {
						this.createEndpoint();
					}
				} else {
					return false;
				}
			});
		},

		async createEndpoint() {
			if (this.loading) {
				return;
			}

			this.loading = true;

			let payload = {
				participant: this.participant,
				legal_entity: this.legalEntity,
				recipient: this.recipient,
				contact: this.contact,
				channel: this.form.channel,
				language: this.form.language,
				email_endpoint_details: null as (null | EmailEndpointDetails),
				phone_endpoint_details: null as (null | PhoneEndpointDetails),
				mail_endpoint_details: null as (null | MailEndpointDetails)
			};
			switch (this.form.channel) {
				case Risolto_Channel_Enum.Email:
					payload = {
						...payload,
						email_endpoint_details: {
							email: this.form.email,
							invoicing: this.form.invoicing,
							invoicing_type: this.form.invoicing_type,
							dunning: this.form.dunning,
							dunning_type: this.form.dunning_type
						}
					};
					break;
				case Risolto_Channel_Enum.Phone:
					payload = {
						...payload,
						phone_endpoint_details: {
							telephone_number: this.form.telephone_number,
							invoicing: this.form.invoicing,
							dunning: this.form.dunning
						}
					};
					break;
				case Risolto_Channel_Enum.Mail:
					payload = {
						...payload,
						mail_endpoint_details: {
							address: {
								city: this.form.city,
								zip_code: this.form.zip_code,
								street_name: this.form.street_name,
								street_number: this.form.street_number,
								post_box: this.form.post_box,
								iso_country_code: this.form.iso_country_code,
								company_name: this.form.company_name
							},
							invoicing: this.form.invoicing,
							dunning: this.form.dunning
						}
					};
					break;
			}

			try {
				await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/channel-endpoint`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.handleClose();
				this.$store.commit('apollo/RISOLTO_CHANNEL_ENDPOINT');
			} catch (e) {
				this.$message.error(this.$t('somethingWentWrong'));
			} finally {
				this.loading = false;
			}
		},

		async updateEndpoint() {
			if (this.loading) {
				return;
			}

			this.loading = true;

			let channelEndpoint = {
				reference: this.form.reference,
				channel: this.form.channel,
				endpoint: (() => {
					switch (this.form.channel) {
						case Risolto_Channel_Enum.Email:
							return this.form.email;
						case Risolto_Channel_Enum.Phone:
							return this.form.telephone_number;
						case Risolto_Channel_Enum.Mail:
							return this.form.street_name;
						default:
							return null;
					}
				})(),
				endpoint_details: (() => {
					switch (this.form.channel) {
						case Risolto_Channel_Enum.Email:
							return {
								email: this.form.email,
								invoicing: this.form.invoicing,
								invoicing_type: this.form.invoicing_type,
								dunning: this.form.dunning,
								dunning_type: this.form.dunning_type
							} as EmailEndpointDetails;
						case Risolto_Channel_Enum.Phone:
							return {
								telephone_number: this.form.telephone_number,
								invoicing: this.form.invoicing,
								dunning: this.form.dunning
							} as PhoneEndpointDetails;
						case Risolto_Channel_Enum.Mail:
							return {
								address: {
									city: this.form.city,
									zip_code: this.form.zip_code,
									street_name: this.form.street_name,
									street_number: this.form.street_number,
									post_box: this.form.post_box,
									iso_country_code: this.form.iso_country_code,
									company_name: this.form.company_name
								},
								invoicing: this.form.invoicing,
								dunning: this.form.dunning
							} as MailEndpointDetails;
						default:
							return null;
					}
				})(),
				locale: this.form.language
			};

			await recipientUpdateChannelEndpoint(
				this.participant,
				this.legalEntity,
				this.recipient,
				channelEndpoint
			);

			this.loading = false;

			this.handleClose();
			this.$store.commit('apollo/RISOLTO_CHANNEL_ENDPOINT');
		}
	}
});
